import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

/**
 * Bank picker modal — lists candidate questions from the Question Bank for
 * the given paper + slot metadata (CO / BL / Unit / Marks), lets the user
 * search and pick one to replace the current slot's questionText.
 *
 * Props:
 *   isOpen
 *   onClose
 *   paperId
 *   slot            — { co, bl, unit, marks } of the current question slot
 *   onPick(html)    — called with the picked question's HTML
 */
function BankPickerModal({ isOpen, onClose, paperId, slot, onPick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [relaxed, setRelaxed] = useState(false); // if true, drop slot filters

  const load = useCallback(async () => {
    if (!isOpen || !paperId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (!relaxed) {
        if (slot?.co) params.set('co', slot.co);
        if (slot?.bl) params.set('bl', slot.bl);
        if (slot?.unit) params.set('unit', slot.unit);
        if (slot?.marks) params.set('marks', slot.marks);
      }
      if (search.trim()) params.set('q', search.trim());
      params.set('limit', '100');
      const res = await api.get(`/qp-generation/papers/${paperId}/bank-candidates?${params.toString()}`);
      if (res.data.success) setRows(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load bank questions');
    } finally {
      setLoading(false);
    }
  }, [isOpen, paperId, slot, search, relaxed]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setRelaxed(false);
    }
  }, [isOpen]);

  useEffect(() => { load(); }, [load]);

  if (!isOpen) return null;

  const slotChips = [];
  if (slot?.co) slotChips.push(`CO: ${slot.co}`);
  if (slot?.bl) slotChips.push(`BL: ${slot.bl}`);
  if (slot?.unit) slotChips.push(`Unit: ${slot.unit}`);
  if (slot?.marks) slotChips.push(`${slot.marks} marks`);

  return (
    <div className="qpg-modal-backdrop" onClick={onClose}>
      <div
        className="qpg-modal"
        style={{ maxWidth: 880, width: '95%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qpg-modal-header">
          <div>
            <h3>Pick from Question Bank</h3>
            <div className="qpg-modal-sub">
              Matching{' '}
              {slotChips.length > 0 ? (
                <>
                  {slotChips.map((c, i) => (
                    <span key={i} className="qpg-meta-chip" style={{ marginRight: 4 }}>{c}</span>
                  ))}
                </>
              ) : 'all questions'}
              {relaxed && ' — filters relaxed'}
            </div>
          </div>
          <button className="qpg-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="qpg-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search question text..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className={`btn ${relaxed ? 'btn-primary' : 'btn-outline'} btn-sm`}
              onClick={() => setRelaxed((r) => !r)}
            >
              {relaxed ? 'Apply slot filters' : 'Ignore slot filters'}
            </button>
          </div>

          {loading ? (
            <div className="qpg-empty">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="qpg-empty">
              No matching questions in the bank. Try searching, or click
              {' '}<strong>Ignore slot filters</strong> above.
            </div>
          ) : (
            <div>
              {rows.map((q) => (
                <div
                  key={q.QuestionID}
                  className="qpg-bank-row"
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 8,
                    background: '#fafafa',
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
                  >
                    <div style={{ flex: 1, fontSize: 14 }} dangerouslySetInnerHTML={{ __html: q.QuestionHtml }} />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => onPick(q.QuestionHtml)}
                    >
                      Use this
                    </button>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {q.Marks != null && <span className="qpg-meta-chip">{q.Marks} marks</span>}
                    {q.CO && <span className="qpg-meta-chip">CO: {q.CO}</span>}
                    {q.Unit && <span className="qpg-meta-chip">Unit: {q.Unit}</span>}
                    {q.BL && <span className="qpg-meta-chip">BL: {q.BL}</span>}
                    {q.Difficulty && <span className="qpg-meta-chip">{q.Difficulty}</span>}
                    {q.QType && <span className="qpg-meta-chip">{q.QType}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="qpg-modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default BankPickerModal;
